// Per-tab persistence for the submission flow's identity.
//
// Deliberately dependency-free (sessionStorage only, no API imports) so the
// entry guard and the Rematch model can both use it without dragging the API
// layer along.
//
// Only the flow's identity is stored — never the breakdown snapshot, which
// must be re-fetched on rehydrate so the expired-breakdown gate re-evaluates.

const STORAGE_KEY = "cwbSubmissionFlow.v1";
const FLOW_VERSION = 1;
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

// Every flow targets a breakdown (`bd` is mandatory), so there is no
// role-less entry source.
export type SubmissionEntrySource = "boost" | "castingCalls" | "direct";

export interface IPersistedFlow {
  flowVersion: number;
  entrySource: SubmissionEntrySource;
  // Raw `bd` / `roleid` from the URL. Strings: the breakdown ref is zero-padded
  // (bd=0175445) and must round-trip unchanged; the role is the bare id.
  breakdownId: string | null;
  roleId: string | null;
  currentStep: number;
  startedAtUtc: string;
}

export const writePersistedFlow = (flow: {
  entrySource: SubmissionEntrySource;
  breakdownId: string | null;
  roleId: string | null;
  currentStep: number;
  startedAtUtc?: string | null;
}) => {
  try {
    const persisted: IPersistedFlow = {
      flowVersion: FLOW_VERSION,
      entrySource: flow.entrySource,
      breakdownId: flow.breakdownId,
      roleId: flow.roleId,
      currentStep: flow.currentStep,
      startedAtUtc: flow.startedAtUtc || new Date().toISOString(),
    };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
  } catch (e) {
    // Storage unavailable (private mode/quota): the flow still works in-page,
    // it just won't survive a refresh.
  }
};

export const readPersistedFlow = (): IPersistedFlow | null => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as IPersistedFlow;
    const tooOld =
      !parsed.startedAtUtc ||
      Date.now() - Date.parse(parsed.startedAtUtc) > MAX_AGE_MS;
    if (parsed.flowVersion !== FLOW_VERSION || tooOld || !parsed.entrySource) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch (e) {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (e2) {
      // ignore
    }
    return null;
  }
};

export const clearPersistedFlow = () => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // ignore
  }
};

// True when this tab has a flow that was started properly — either targeting a
// breakdown, or the deliberate role-less entry from the expired gate.
export const hasPersistedFlow = (): boolean => !!readPersistedFlow();
